const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

// After signOut, redirect back to the Platform-Admin sign-in page (relative
// path — Clerk handles the navigation).
const POST_LOGOUT_URI = '/signin';

type GetAccessToken = () => Promise<string | null | undefined>;
type SignOut = (options?: { redirectUrl?: string }) => Promise<void>;

let _getAccessToken: GetAccessToken | null = null;
let _signOut: SignOut | null = null;

/**
 * Called once from a React component to wire up Clerk's `getToken` function
 * so plain service modules can obtain Bearer tokens without hooks.
 */
export function setAccessTokenGetter(fn: GetAccessToken) {
  _getAccessToken = fn;
}

/**
 * Called once from a React component to wire up Clerk's `signOut` function
 * so the retry wrapper can sign out on persistent auth failure.
 */
export function setSignOutFunction(fn: SignOut) {
  _signOut = fn;
}

/**
 * Authenticated fetch wrapper. Attaches `Authorization: Bearer <token>` from
 * the Clerk session. Token-fetch failures are non-fatal (the request just
 * goes out unauthenticated and the server's auth guard returns 401).
 *
 * @param path  path relative to API_BASE_URL, e.g. "/v1/schools"
 * @param init  standard RequestInit
 */
export async function apiFetch(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const headers = new Headers(init.headers);

  if (_getAccessToken) {
    try {
      const token = await _getAccessToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
    } catch {
      // Token fetch failed — proceed without auth header
    }
  }

  const hasBody = init.body !== undefined && init.body !== null;
  const isFormData = typeof FormData !== 'undefined' && init.body instanceof FormData;

  if (hasBody && !isFormData && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  return fetch(`${API_BASE_URL}${path}`, { ...init, headers });
}

/**
 * Authenticated fetch wrapper with one auto-retry on 401/403.
 *
 * Clerk's `getToken()` caches and auto-refreshes; calling again on a fresh
 * tick triggers a refresh if the cached token is expiring. If the retry
 * also fails with 401/403 and we're not already on the sign-in page, sign
 * the user out and redirect back to /signin.
 */
export async function apiFetchWithRetry(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const response = await apiFetch(path, init);

  if (response.ok || (response.status !== 401 && response.status !== 403)) {
    return response;
  }

  console.debug(`[apiFetchWithRetry] ${response.status} detected, attempting token refresh...`);

  if (!_getAccessToken) {
    console.error('[apiFetchWithRetry] getAccessToken not available, cannot retry');
    return response;
  }

  try {
    const freshToken = await _getAccessToken();

    if (!freshToken) {
      console.debug('[apiFetchWithRetry] Token refresh returned nothing — letting caller handle');
      return response;
    }

    const retryHeaders = new Headers(init.headers);
    retryHeaders.set('Authorization', `Bearer ${freshToken}`);

    const hasBody = init.body !== undefined && init.body !== null;
    const isFormData = typeof FormData !== 'undefined' && init.body instanceof FormData;

    if (hasBody && !isFormData && !retryHeaders.has('Content-Type')) {
      retryHeaders.set('Content-Type', 'application/json');
    }

    const retryResponse = await fetch(`${API_BASE_URL}${path}`, {
      ...init,
      headers: retryHeaders,
    });

    if (retryResponse.status === 401 || retryResponse.status === 403) {
      const isOnSignIn = typeof window !== 'undefined' && window.location.pathname.startsWith('/signin');
      if (!isOnSignIn) {
        console.debug('[apiFetchWithRetry] Retry failed — signing out');
        await new Promise((resolve) => setTimeout(resolve, 100));
        // A 403 after a valid token = authenticated but wrong role (wrong
        // portal). Flag it so the sign-in screen explains the access denial;
        // a 401 is just an expired/invalid session → land on plain /signin.
        const redirectUrl = retryResponse.status === 403 ? `${POST_LOGOUT_URI}?denied=1` : POST_LOGOUT_URI;
        await _signOut?.({ redirectUrl });
      }
    }

    return retryResponse;
  } catch (error) {
    console.error('[apiFetchWithRetry] Error during token refresh:', error);
    const isOnSignIn = typeof window !== 'undefined' && window.location.pathname.startsWith('/signin');
    if (!isOnSignIn) {
      await _signOut?.({ redirectUrl: POST_LOGOUT_URI });
    }
    return response;
  }
}
